import React, { useEffect, useMemo, useState } from 'react';
import { useFetch } from '../../hooks/useFetch';
import DirectorPanelState from './DirectorPanelState';
import { formatDateDisplay as formatDate } from '../../utils/formatDate';
import { withQuery } from '../../utils/buildQuery';

const ALL_DIRECTOR_LOCATIONS = 'ALL';

function formatNumber(value) {
  const numericValue = Number(value || 0);
  return Number.isInteger(numericValue) ? String(numericValue) : numericValue.toFixed(2);
}

function getTenureBucket(yearsOfService) {
  const numericYears = Number(yearsOfService || 0);

  if (numericYears < (4 / 12)) {
    return 'Under 4 Months';
  }

  if (numericYears < 5) {
    return '4 Months to 4 Years';
  }

  if (numericYears < 10) {
    return '5 Years to 9 Years';
  }

  if (numericYears < 20) {
    return '10 Years to 19 Years';
  }

  return '20+ Years';
}

function getFlagTone(flag) {
  const normalizedFlag = String(flag || '').toLowerCase();

  if (normalizedFlag === 'above benchmark') {
    return 'denied';
  }

  if (normalizedFlag === 'near benchmark') {
    return 'warning';
  }

  return 'approved';
}

function compareValues(left, right, direction = 'asc') {
  if (typeof left === 'number' && typeof right === 'number') {
    return direction === 'asc' ? left - right : right - left;
  }

  return direction === 'asc'
    ? String(left || '').localeCompare(String(right || ''))
    : String(right || '').localeCompare(String(left || ''));
}

function SortableHeader({ label, column, sortColumn, sortDirection, onSort }) {
  const active = sortColumn === column;
  return (
    <th>
      <button
        type="button"
        className={`director-sort-button ${active ? 'active' : ''}`}
        onClick={() => onSort(column)}
      >
        {label}
        <span>{active ? (sortDirection === 'asc' ? '^' : 'v') : '<>'}</span>
      </button>
    </th>
  );
}

function DirectorBenchmarkPanel({ selectedLocationCode, locationLabel }) {
  const [selectedManagerId, setSelectedManagerId] = useState('');
  const [sortColumn, setSortColumn] = useState('varianceVsBenchmark');
  const [sortDirection, setSortDirection] = useState('desc');

  const benchmarkUrl = useMemo(() => withQuery('/api/benchmarks/vacation', {
    locationCode: selectedLocationCode && selectedLocationCode !== ALL_DIRECTOR_LOCATIONS ? selectedLocationCode : null
  }), [selectedLocationCode]);

  const benchmarkRequestOptions = useMemo(
    () => ({ timeoutMs: selectedLocationCode === ALL_DIRECTOR_LOCATIONS ? 25000 : 18000 }),
    [selectedLocationCode]
  );

  const { data: payload, isLoading, error: errorMessage } = useFetch(benchmarkUrl, {
    requestOptions: benchmarkRequestOptions,
  });

  const rows = useMemo(
    () => (Array.isArray(payload?.rows) ? payload.rows : []),
    [payload]
  );

  useEffect(() => {
    setSelectedManagerId((currentSelection) => {
      if (currentSelection && rows.some(row => String(row.managerId) === String(currentSelection))) {
        return currentSelection;
      }
      return rows[0] ? String(rows[0].managerId) : '';
    });
  }, [rows]);

  const benchmarkSummary = useMemo(() => ({
    within: rows.filter(row => row.benchmarkFlag === 'Within Benchmark').length,
    near: rows.filter(row => row.benchmarkFlag === 'Near Benchmark').length,
    above: rows.filter(row => row.benchmarkFlag === 'Above Benchmark').length
  }), [rows]);

  const sortedRows = useMemo(() => {
    const flagRank = {
      'Above Benchmark': 3,
      'Near Benchmark': 2,
      'Within Benchmark': 1
    };

    return [...rows].sort((left, right) => {
      if (sortColumn === 'benchmarkFlag') {
        return compareValues(flagRank[left.benchmarkFlag] || 0, flagRank[right.benchmarkFlag] || 0, sortDirection);
      }

      if (sortColumn === 'tenureBucket') {
        return compareValues(Number(left.yearsOfService || 0), Number(right.yearsOfService || 0), sortDirection);
      }

      if (sortColumn === 'hireDate') {
        const leftDate = left.hireDate ? new Date(left.hireDate).getTime() : 0;
        const rightDate = right.hireDate ? new Date(right.hireDate).getTime() : 0;
        return compareValues(leftDate, rightDate, sortDirection);
      }

      return compareValues(left[sortColumn], right[sortColumn], sortDirection);
    });
  }, [rows, sortColumn, sortDirection]);

  const selectedManager = useMemo(
    () => sortedRows.find(row => String(row.managerId) === String(selectedManagerId)) || null,
    [sortedRows, selectedManagerId]
  );

  useEffect(() => {
    if (!sortedRows.some(row => String(row.managerId) === String(selectedManagerId))) {
      setSelectedManagerId(sortedRows[0] ? String(sortedRows[0].managerId) : '');
    }
  }, [selectedManagerId, sortedRows]);

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(current => (current === 'asc' ? 'desc' : 'asc'));
      return;
    }

    setSortColumn(column);
    setSortDirection(
      column === 'managerName' || column === 'locationCode' || column === 'benchmarkFlag'
        ? 'asc'
        : 'desc'
    );
  };

  return (
    <div className="role-section">
      <div className="role-section-header">
        <h2>Benchmark Vacation Reference</h2>
        <div className="role-section-actions">
          <span>{locationLabel}</span>
        </div>
      </div>
      <p className="role-section-copy">
        Director-only reference view for approved and scheduled vacation against the annual benchmark.
      </p>
      <p className="director-muted">Use Data Management &amp; Audit for benchmark CSV export.</p>

      <div className="role-benchmark-summary-row">
        <div className="role-benchmark-summary-card">
          <span>Managers In Scope</span>
          <strong>{rows.length}</strong>
        </div>
        <div className="role-benchmark-summary-card">
          <span>Within Benchmark</span>
          <strong>{benchmarkSummary.within}</strong>
        </div>
        <div className="role-benchmark-summary-card">
          <span>Near Benchmark</span>
          <strong>{benchmarkSummary.near}</strong>
        </div>
        <div className="role-benchmark-summary-card">
          <span>Above Benchmark</span>
          <strong>{benchmarkSummary.above}</strong>
        </div>
      </div>

      <DirectorPanelState kind="loading" message={isLoading ? `Loading benchmark vacation reference for ${locationLabel}...` : ''} />
      <DirectorPanelState kind="error" message={errorMessage} />

      {!isLoading && !errorMessage && (
        <>
          <div className="table-card">
            <table className="role-table director-benchmark-table">
              <thead>
                <tr>
                  <SortableHeader className="director-benchmark-col-manager" label="Manager" column="managerName" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                  <SortableHeader className="director-benchmark-col-station" label="Location" column="locationCode" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                  <SortableHeader className="director-benchmark-col-hire-date" label="Hire Date" column="hireDate" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                  <SortableHeader className="director-benchmark-col-tenure" label="Tenure Bucket" column="tenureBucket" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                  <SortableHeader className="director-benchmark-col-days" label="Benchmark Annual Days" column="benchmarkAnnualDays" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                  <SortableHeader className="director-benchmark-col-days" label="Approved YTD" column="approvedDaysTakenYtd" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                  <SortableHeader className="director-benchmark-col-days" label="Scheduled" column="scheduledUpcomingDays" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                  <SortableHeader className="director-benchmark-col-days" label="Total" column="totalApprovedAndScheduled" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                  <SortableHeader className="director-benchmark-col-variance" label="Variance" column="varianceVsBenchmark" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                  <SortableHeader className="director-benchmark-col-flag" label="Flag" column="benchmarkFlag" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                </tr>
              </thead>
              <tbody>
                {sortedRows.map(row => (
                  <tr
                    key={row.managerId}
                    className={String(selectedManagerId) === String(row.managerId) ? 'role-request-selected' : ''}
                    onClick={() => setSelectedManagerId(String(row.managerId))}
                  >
                    <td className="director-benchmark-col-manager">{row.managerName}</td>
                    <td className="director-benchmark-col-station">{row.locationCode}</td>
                    <td className="director-benchmark-col-hire-date">{formatDate(row.hireDate)}</td>
                    <td className="director-benchmark-col-tenure">{getTenureBucket(row.yearsOfService)}</td>
                    <td className="director-benchmark-col-days">{formatNumber(row.benchmarkAnnualDays)}</td>
                    <td className="director-benchmark-col-days">{formatNumber(row.approvedDaysTakenYtd)}</td>
                    <td className="director-benchmark-col-days">{formatNumber(row.scheduledUpcomingDays)}</td>
                    <td className="director-benchmark-col-days">{formatNumber(row.totalApprovedAndScheduled)}</td>
                    <td className="director-benchmark-col-variance">{formatNumber(row.varianceVsBenchmark)}</td>
                    <td className="director-benchmark-col-flag">
                      <span className={`role-status-pill ${getFlagTone(row.benchmarkFlag)}`}>
                        {row.benchmarkFlag}
                      </span>
                    </td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr>
                    <td colSpan="10">No benchmark vacation rows are available for this scope yet.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="role-request-detail">
            <div className="role-request-detail-card">
              <div className="role-section-header">
                <h2>Selected Benchmark Detail</h2>
                <span>{selectedManager ? selectedManager.managerName : 'None selected'}</span>
              </div>
              {selectedManager ? (
                <div className="role-request-detail-grid">
                  <p><strong>Hire Date</strong><span>{formatDate(selectedManager.hireDate)}</span></p>
                  <p><strong>Tenure Bucket</strong><span>{getTenureBucket(selectedManager.yearsOfService)}</span></p>
                  <p><strong>Benchmark Annual Days</strong><span>{formatNumber(selectedManager.benchmarkAnnualDays)}</span></p>
                  <p><strong>Approved YTD</strong><span>{formatNumber(selectedManager.approvedDaysTakenYtd)}</span></p>
                  <p><strong>Scheduled Upcoming</strong><span>{formatNumber(selectedManager.scheduledUpcomingDays)}</span></p>
                  <p><strong>Total Planned</strong><span>{formatNumber(selectedManager.totalApprovedAndScheduled)}</span></p>
                  <p><strong>Variance</strong><span>{formatNumber(selectedManager.varianceVsBenchmark)}</span></p>
                  <p><strong>Flag</strong><span className={`role-status-pill ${getFlagTone(selectedManager.benchmarkFlag)}`}>{selectedManager.benchmarkFlag}</span></p>
                </div>
              ) : (
                <p className="role-section-copy">Select a manager row to review the benchmark reference details.</p>
              )}
            </div>
            <div className="role-history-panel">
              <div className="role-history-header">
                <h3>Reference Notes</h3>
                <span>Informational Only</span>
              </div>
              <p className="role-section-copy">
                Benchmark output is director-only and does not change the approval-based vacation workflow.
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default DirectorBenchmarkPanel;
